import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Menu, message, Space } from "antd";
import { HOURLY_PACKING_LIST_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import { isMobileView } from "components/Common/AppHelper";
import CustomFilter from "components/Common/CustomFilter";
import Header from "components/Common/Header";
import ListTable from "common/ListTable";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PACKING_EDIT_PERMISSION } from "routes/permissions";
import { hasPermission } from "utils/functions";

const HourlyPackingConfirmationList = ({ width }) => {
  // Router
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location?.pathname;

  // States
  const [showFilter, setShowFilter] = useState(() => {
    const filterStatus = localStorage.getItem("filter_status");
    return filterStatus && routerPath ? JSON.parse(filterStatus)[routerPath] : false;
  });
  const [listData, setListData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState(10);
  const [fetchError, setFetchError] = useState(null);

  // Fetch List Data
  const getListData = useCallback(async (filter = {}) => {
    setFetchError(null);
    setLoading(true);
    try {
      // Clean up filter - remove empty strings and null values
      const cleanedFilter = Object.keys(filter).reduce((acc, key) => {
        if (filter[key] !== null && filter[key] !== undefined && filter[key] !== "") {
          acc[key] = filter[key];
        }
        return acc;
      }, {});

      const response = await getData(HOURLY_PACKING_LIST_ENDPOINT, false, cleanedFilter);
      console.log("Hourly Packing Confirmation List Data:", response);
      if (response?.status === 200) {
        const masterData = response?.data?.data || response;
        const dataArray = Array.isArray(masterData?.data) ? masterData.data : [];
        setListData(dataArray);
        setTotalData(dataArray.length);
        setRangeData(10);
      } else {
        setFetchError("Could not load this list.");
        message.error("Error getting hourly packing confirmation data");
      }
    } catch (error) {
      setFetchError("Could not load this list.");
      console.error("Error fetching data:", error);
      message.error("Error fetching hourly packing confirmation data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Menu for Actions
  const menu = useCallback(
    (data) => (
      <Menu
        items={[
          {
            label: (
              <Button
                icon={<EditOutlined />}
                type="text"
                size="small"
                onClick={() => navigate(`/digitalization/packing/confirmation/edit/${data?.id}`)}
              >
                Edit
              </Button>
            ),
            key: "1",
            disabled: !hasPermission([PACKING_EDIT_PERMISSION]),
          },
        ]}
      />
    ),
    [navigate],
  );

  // Memoized Columns
  const columns = useMemo(
    () => [
      {
        title: "Entry Date",
        dataIndex: "entry_date",
        key: "entry_date",
        render: (text) => (text ? moment(text).format("YYYY-MM-DD") : "-"),
      },
      {
        title: "Hour",
        dataIndex: "hour",
        key: "hour",
      },
      {
        title: "Time Range",
        key: "time_range",
        render: (_, record) => {
          return `${record?.hour_time_range_from || ""} - ${record?.hour_time_range_to || ""}`;
        },
      },
      {
        title: "Packing Line",
        dataIndex: ["packing_line_info", "name"],
        key: "packing_line",
      },
      {
        title: "Packing Table",
        dataIndex: ["packing_table_info", "name"],
        key: "packing_table",
      },
      {
        title: "Operator",
        dataIndex: ["operator_info", "name"],
        key: "operator",
      },
      {
        title: "Sales Contract",
        dataIndex: ["sales_contract_info", "reference_no"],
        key: "sales_contract",
      },
      {
        title: "Buying Office",
        dataIndex: ["buying_office_info", "name"],
        key: "buying_office",
      },
      {
        title: "Buyer",
        dataIndex: ["buyer_info", "name"],
        key: "buyer",
      },
      {
        title: "PO",
        dataIndex: ["po_map_info", "original_po"],
        key: "po",
      },
      {
        title: "Actions",
        key: "action",
        width: "10%",
        render: (_, record) => (
          <div className="cursor-pointer">
            <Dropdown overlay={menu(record)}>
              <MoreOutlined style={{ fontSize: "14px" }} />
            </Dropdown>
          </div>
        ),
      },
    ],
    [menu],
  );

  // Pagination Handler
  const paginate = useCallback((page) => setCurrentPage(page), []);

  // Memoized Filter
  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={listData}
        fetchTableData={getListData}
        filterName="hourly_packing_confirmation"
      />
    ),
    [showFilter],
  );

  // Update Filter Status in LocalStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    parsedFilterStatus[routerPath] = showFilter;
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath]);

  // Fetch Data on Mount
  useEffect(() => {
    getListData();
  }, [getListData]);

  return (
    <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{
            width: `${isMobileView() ? "100%" : `${width}px`}`,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <Header
            title="Hourly Packing Confirmation List"
            subTitle=""
            actions={
              <Space>
                <Button
                  type={showFilter ? "danger" : "primary"}
                  size="small"
                  onClick={() => setShowFilter((prev) => !prev)}
                >
                  {`${showFilter ? "Close" : "Open"} Filter Option`}
                </Button>
              </Space>
            }
          />
          <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>{memoizedCustomFilter}</div>
        </Card>
      </Affix>

      <div className="containt-body" style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <Card style={{ width: "100%", maxWidth: "100%" }}>
          <div style={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
            <ListTable
              loading={loading}
              error={fetchError}
              onRetry={() => getListData()}
              emptyText="No records to show yet."
              dataSource={listData}
              columns={columns}
              scroll={{ x: "max-content" }}
              rowKey="id"
              pagination={{
                showSizeChanger: false,
                current: currentPage,
                total: totalData,
                pageSize: rangeData,
                onChange: paginate,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                hideOnSinglePage: totalData <= rangeData,
              }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HourlyPackingConfirmationList;
